import db from '../db';
import { supabase } from '../supabaseClient';

/**
 * Sync all pending offline data to Supabase when online.
 * Silently skips items that fail RLS (e.g. "not linked to employee").
 * Deletes items that are permanently invalid to prevent infinite retry loops.
 */
let syncLock = false; // Prevent concurrent syncs

export async function syncAllPending() {
  if (!navigator.onLine) return { punches: 0, checkpoints: 0, claims: 0, leaves: 0, errors: [] };
  if (syncLock) { console.log('[OfflineSync] Already syncing, skipping'); return null; }
  syncLock = true;

  const results = { punches: 0, checkpoints: 0, claims: 0, leaves: 0, errors: [] };

  // All sync sections are individually try-caught so one failure doesn't
  // prevent the others from running. The outer try/finally guarantees the
  // syncLock is always released.
  try {
  const recomputedDates = new Map(); // dateKey → [p.id, ...] for cleanup after RPC
  try {
    const punches = await db.pendingPunches.toArray();
    for (const p of punches) {
      try {
        const { error } = await supabase.from('attendance_punches').insert({
          employee_id: p.employee_id,
          company_id: p.company_id || null,
          punch_date: p.punch_date,
          type: p.type,
          punched_at: p.punched_at,
          lat: p.lat || null,
          lng: p.lng || null,
          accuracy_m: p.accuracy_m || null,
          source: 'webapp-offline',
        });
        if (!error) {
          results.punches++;
          // Track for recomputation — don't delete from IndexedDB yet
          const dateKey = `${p.employee_id}:${p.punch_date}`;
          if (!recomputedDates.has(dateKey)) recomputedDates.set(dateKey, []);
          recomputedDates.get(dateKey).push(p.id);
        } else {
          if (error.code === '42501' || error.message?.includes('violates')) {
            console.warn('[OfflineSync] Punch blocked by RLS, discarding:', error.message);
            await db.pendingPunches.delete(p.id);
          }
          results.errors.push({ type: 'punch', error: error.message });
        }
      } catch (err) {
        results.errors.push({ type: 'punch', error: err.message });
      }
    }
    // Recompute attendance_days — only delete from IndexedDB AFTER RPC succeeds
    for (const [dateKey, punchIds] of recomputedDates) {
      const [empId, punchDate] = dateKey.split(':');
      try {
        await supabase.rpc('compute_attendance_day', {
          p_employee_id: empId,
          p_date: punchDate,
        });
        // RPC succeeded — safe to clean up IndexedDB
        await db.pendingPunches.bulkDelete(punchIds);
      } catch (rpcErr) {
        console.warn('[OfflineSync] compute_attendance_day failed for', dateKey, rpcErr.message);
        // Punch stays in IndexedDB for next retry
      }
    }
  } catch (err) {
    results.errors.push({ type: 'punches', error: err.message });
  }

  // Sync checkpoints
  try {
    const checkpoints = await db.pendingCheckpoints.toArray();
    for (const c of checkpoints) {
      try {
        const { error } = await supabase.from('trip_checkpoints').insert({
          trip_id: c.trip_id,
          type: c.type,
          captured_at: c.captured_at,
          latitude: c.latitude,
          longitude: c.longitude,
        });
        if (!error) {
          await db.pendingCheckpoints.delete(c.id);
          results.checkpoints++;
        } else {
          results.errors.push({ type: 'checkpoint', error: error.message });
        }
      } catch (err) {
        results.errors.push({ type: 'checkpoint', error: err.message });
      }
    }
  } catch (err) {
    results.errors.push({ type: 'checkpoints', error: err.message });
  }

  // Sync claims
  try {
    const claims = await db.pendingClaims.toArray();
    for (const c of claims) {
      try {
        const { error } = await supabase.from('expense_claims').insert({
          employee_id: c.employee_id,
          claim_type: c.claim_type,
          claim_date: c.claim_date,
          amount: c.amount,
          vendor: c.vendor,
          description: c.description,
          status: 'pending',
        });
        if (!error) {
          await db.pendingClaims.delete(c.id);
          results.claims++;
        } else {
          // "not linked to employee" or other RLS = permanently invalid
          if (error.code === '42501' || error.message?.includes('violates') || error.message?.includes('not linked')) {
            console.warn('[OfflineSync] Claim blocked by RLS, discarding:', error.message);
            await db.pendingClaims.delete(c.id);
          }
          results.errors.push({ type: 'claim', error: error.message });
        }
      } catch (err) {
        results.errors.push({ type: 'claim', error: err.message });
      }
    }
  } catch (err) {
    results.errors.push({ type: 'claims', error: err.message });
  }

  // Sync leave applications
  try {
    const leaves = await db.pendingLeave.toArray();
    for (const l of leaves) {
      try {
        const { error } = await supabase.from('leave_applications').insert({
          employee_id: l.employee_id,
          leave_type_code: l.leave_type_code,
          start_date: l.start_date,
          end_date: l.end_date,
          is_half_day: l.is_half_day || false,
          half_day_period: l.half_day_period || null,
          reason: l.reason || null,
          attachment_urls: l.attachment_urls || null,
          status: 'pending',
        });
        if (!error) {
          await db.pendingLeave.delete(l.id);
          results.leaves++;
        } else {
          if (error.code === '42501' || error.message?.includes('violates')) {
            console.warn('[OfflineSync] Leave blocked by RLS, discarding:', error.message);
            await db.pendingLeave.delete(l.id);
          }
          results.errors.push({ type: 'leave', error: error.message });
        }
      } catch (err) {
        results.errors.push({ type: 'leave', error: err.message });
      }
    }
  } catch (err) {
    results.errors.push({ type: 'leaves', error: err.message });
  }

  } finally { // eslint-disable-line no-unsafe-finally
    syncLock = false;
  }

  return results;
}

// ─── Save offline helpers ────────────────────────────────────────────────

export async function savePunchOffline(punch) {
  return await db.pendingPunches.add({
    employee_id: punch.employee_id,
    company_id: punch.company_id || null,
    type: punch.type,
    punch_date: punch.punch_date,
    punched_at: punch.punched_at || new Date().toISOString(),
    lat: punch.lat || null,
    lng: punch.lng || null,
    accuracy_m: punch.accuracy_m || null,
    source: 'webapp-offline',
  });
}

export async function saveCheckpointOffline(checkpoint) {
  return await db.pendingCheckpoints.add({
    trip_id: checkpoint.trip_id,
    type: checkpoint.type,
    captured_at: checkpoint.captured_at || new Date().toISOString(),
    latitude: checkpoint.latitude,
    longitude: checkpoint.longitude,
  });
}

export async function saveClaimOffline(claim) {
  return await db.pendingClaims.add({
    employee_id: claim.employee_id,
    claim_type: claim.claim_type,
    claim_date: claim.claim_date || new Date().toISOString().split('T')[0],
    amount: claim.amount,
    vendor: claim.vendor,
    description: claim.description,
  });
}

export async function saveLeaveOffline(leave) {
  return await db.pendingLeave.add({
    employee_id: leave.employee_id,
    leave_type_code: leave.leave_type_code,
    start_date: leave.start_date,
    end_date: leave.end_date,
    is_half_day: leave.is_half_day || false,
    half_day_period: leave.half_day_period || null,
    reason: leave.reason || null,
    attachment_urls: leave.attachment_urls || null,
  });
}

export async function getPendingCount() {
  const [punches, checkpoints, claims, leaves] = await Promise.all([
    db.pendingPunches.count(),
    db.pendingCheckpoints.count(),
    db.pendingClaims.count(),
    db.pendingLeave.count(),
  ]);
  return { punches, checkpoints, claims, leaves, total: punches + checkpoints + claims + leaves };
}

// ─── Cache helpers ───────────────────────────────────────────────────────

export async function cacheAttendanceRows(rows) {
  if (!rows || rows.length === 0) return;
  await db.cachedAttendance.bulkPut(
    rows.map((r) => ({
      employee_id: r.employee_id,
      punch_date: r.punch_date,
      first_in: r.first_in,
      last_out: r.last_out,
      worked_min: r.worked_min,
      late_min: r.late_min,
      early_min: r.early_min,
      payable_ot_min: r.payable_ot_min,
      flagged: r.flagged,
    }))
  );
}

export async function getCachedAttendance(employeeId, startDate, endDate) {
  try {
    let query = db.cachedAttendance.where('employee_id').equals(employeeId);
    if (startDate && endDate) {
      query = query.filter((row) => row.punch_date >= startDate && row.punch_date <= endDate);
    }
    return await query.toArray();
  } catch {
    return [];
  }
}

export async function cacheLeaveBalances(employeeId, balances) {
  await db.cachedLeaveBalances.put({ employee_id: employeeId, balances, updated_at: Date.now() });
}

export async function getCachedLeaveBalances(employeeId) {
  try {
    return await db.cachedLeaveBalances.get(employeeId);
  } catch {
    return null;
  }
}

export async function cacheClaims(claims) {
  if (!claims || claims.length === 0) return;
  await db.cachedClaims.bulkPut(
    claims.map((c) => ({
      id: c.id,
      employee_id: c.employee_id,
      status: c.status,
      created_at: c.created_at,
      claim_type: c.claim_type,
      amount: c.amount,
      vendor: c.vendor,
      description: c.description,
    }))
  );
}

export async function getCachedClaims(employeeId) {
  try {
    return await db.cachedClaims.where('employee_id').equals(employeeId).toArray();
  } catch {
    return [];
  }
}

// ─── Auto-sync on reconnect + periodic check ────────────────────────────

let syncInterval = null;

function startAutoSync() {
  if (syncInterval) return; // Already running

  // Sync every 30 seconds if online and has pending items
  syncInterval = setInterval(async () => {
    if (!navigator.onLine) return;
    try {
      const count = await getPendingCount();
      if (count.total > 0) {
        console.log('[OfflineSync] Periodic sync: pending items =', count.total);
        await syncAllPending();
      }
    } catch (err) {
      console.warn('[OfflineSync] Periodic sync failed:', err.message);
    }
  }, 30000);
}

if (typeof window !== 'undefined') {
  // Sync immediately when coming back online
  window.addEventListener('online', () => {
    console.log('[OfflineSync] Back online, syncing pending data...');
    setTimeout(() => {
      syncAllPending().then((results) => {
        const synced = (results?.punches || 0) + (results?.checkpoints || 0) + (results?.claims || 0) + (results?.leaves || 0);
        if (synced > 0) {
          console.log('[OfflineSync] Sync complete:', results);
          // Dispatch event so UI components can refresh
          window.dispatchEvent(new CustomEvent('offline-sync-complete', { detail: results }));
        }
      });
    }, 1000); // 1s delay to let connection stabilize
  });

  // Start periodic sync check
  startAutoSync();
}
